import { Save } from 'lucide-react';
import { observer } from 'mobx-react';
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { MultiCombobox } from '@components/combobox';
import { getCountryLabel } from '@components/country-select';
import { FormSection } from '@components/form-section';
import { PageHeader } from '@components/page-header';
import { Select } from '@components/select';
import { Spinner } from '@components/spinner';
import { StickyBar } from '@components/sticky-bar';
import LocalizedTextArea from '@components/text-area/localized-text-area';
import { TextField } from '@components/textfield';
import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger
} from '@components/ui/accordion';
import { Alert, AlertDescription } from '@components/ui/alert';
import { Button } from '@components/ui/button';
import { Label } from '@components/ui/label';
import { Switch } from '@components/ui/switch';
import { Api } from '@core/api';
import { links } from '@core/router';
import { injectTSDI } from '@core/tsdi';
import {
    I18NText,
    VendorUpdateSupportDto,
    CountryCode,
    VendorSource,
    VendorType,
    VendorCategorySupportDto
} from '@api/WalletClient';
import { useVendorDetailsStore } from '../vendor-details/store';

const SwitchField = ({
    id,
    checked,
    title,
    description,
    onChange
}: {
    id: string;
    checked?: boolean;
    title: string;
    description: string;
    onChange(checked: boolean): void;
}) => (
    <div className="flex items-start gap-3">
        <Switch id={id} checked={checked} onCheckedChange={onChange} />
        <Label htmlFor={id} className="flex flex-col items-start gap-0.5">
            <span className="text-sm">{title}</span>
            <span className="text-xs font-normal text-muted-foreground">
                {description}
            </span>
        </Label>
    </div>
);

const EditVendorForm = observer(() => {
    const { vendorId } = useParams();
    const navigate = useNavigate();

    const { vendor, handleUpdateVendor, updateVendor } =
        useVendorDetailsStore();

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [availableCategories, setAvailableCategories] = useState<
        VendorCategorySupportDto[]
    >([]);
    const [categoriesLoading, setCategoriesLoading] = useState(true);

    const [formData, setFormData] = useState<VendorUpdateSupportDto>({
        name: '',
        image: { objectKey: '', bucketName: '' },
        description: { en: '' },
        redemptionDescription: { en: '' },
        shortDescription: { en: '' },
        supportedCountries: [],
        source: VendorSource.CUSTOM,
        type: VendorType.VOUCHER,
        externalId: '',
        notAvailableReason: { en: '' },
        overrideEnabled: false,
        categoryIds: [],
        displayOrder: 0
    });

    const [notAvailableReasonEnabled, setNotAvailableReasonEnabled] =
        useState(false);
    const [redemptionInstructionsEnabled, setRedemptionInstructionsEnabled] =
        useState(false);

    useEffect(() => {
        const fetchVendor = async () => {
            try {
                setLoading(true);
                const vendorData = await vendor.request([Number(vendorId)]);
                if (vendorData) {
                    setFormData({
                        name: vendorData.name,
                        image: vendorData.image,
                        description: vendorData.description || { en: '' },
                        redemptionDescription:
                            vendorData.redemptionDescription || { en: '' },
                        shortDescription: vendorData.shortDescription || {
                            en: ''
                        },
                        supportedCountries: vendorData.countries || [],
                        source: vendorData.source,
                        type: vendorData.type,
                        externalId: vendorData.externalId || '',
                        notAvailableReason: vendorData.notAvailableReason || {
                            en: ''
                        },
                        overrideEnabled: vendorData.overrideEnabled,
                        categoryIds:
                            vendorData.categories?.map((c) => c.id) || [],
                        displayOrder: vendorData.displayOrder ?? 0
                    });
                    setNotAvailableReasonEnabled(
                        !!vendorData.notAvailableReason
                    );
                    setRedemptionInstructionsEnabled(
                        !!vendorData.redemptionDescription
                    );
                }
            } catch (err: any) {
                console.error('Error fetching vendor:', err);
                setError(
                    err.response?.data?.message ||
                        err.message ||
                        'Failed to fetch vendor'
                );
            } finally {
                setLoading(false);
            }
        };
        fetchVendor();
    }, [vendorId, vendor]);

    useEffect(() => {
        const fetchCategories = async () => {
            try {
                const { walletClient } = injectTSDI(Api);
                const response =
                    await walletClient.supportApi.vendorCategorySupportControllerGetAllCategories();
                const activeCategories = (response.data || [])
                    .filter((c) => !c.deletedDate)
                    .sort((a, b) => a.displayOrder - b.displayOrder);
                setAvailableCategories(activeCategories);
            } catch (err) {
                console.error('Error fetching categories:', err);
            } finally {
                setCategoriesLoading(false);
            }
        };
        fetchCategories();
    }, []);

    const handleI18nTextChange =
        (
            field:
                | 'description'
                | 'shortDescription'
                | 'redemptionDescription'
                | 'notAvailableReason'
        ) =>
        (value: I18NText) => {
            setFormData({
                ...formData,
                [field]: value
            });
        };

    const selectedCategories = availableCategories.filter((c) =>
        formData.categoryIds?.includes(c.id)
    );

    const handleSave = async () => {
        try {
            setError(null);
            const dataToSave = {
                ...formData,
                notAvailableReason: notAvailableReasonEnabled
                    ? formData.notAvailableReason
                    : undefined,
                redemptionDescription: redemptionInstructionsEnabled
                    ? formData.redemptionDescription
                    : undefined
            };
            await handleUpdateVendor(Number(vendorId), dataToSave);
            navigate(links.vendorDetails(Number(vendorId)));
        } catch (err: any) {
            console.error('Error updating vendor:', err);
            setError(
                err.response?.data?.message ||
                    err.message ||
                    'Failed to update vendor'
            );
        }
    };

    const handleCancel = () => {
        navigate(links.vendorDetails(Number(vendorId)));
    };

    const isFormValid = formData.name;

    if (loading) {
        return (
            <div className="flex min-h-[400px] items-center justify-center">
                <Spinner />
            </div>
        );
    }

    if (error && !formData.name) {
        return (
            <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
            </Alert>
        );
    }

    return (
        <>
            <PageHeader
                title="Edit Vendor"
                description={formData.name || 'Update vendor information'}
            />

            <div className="flex flex-col gap-6 pb-24">
                {error && (
                    <Alert variant="destructive">
                        <AlertDescription>{error}</AlertDescription>
                    </Alert>
                )}

                <FormSection title="Basic Information">
                    <div className="flex flex-col gap-4">
                        <SwitchField
                            id="overrideEnabled"
                            checked={formData.overrideEnabled}
                            title="Override Enabled"
                            description="Properties can be updated by third party voucher provider"
                            onChange={(checked) =>
                                setFormData({
                                    ...formData,
                                    overrideEnabled: checked
                                })
                            }
                        />

                        <div className="grid gap-4 md:grid-cols-2">
                            <div>
                                <TextField
                                    label="Vendor Name"
                                    type="text"
                                    value={formData.name}
                                    onChange={(value) =>
                                        setFormData({
                                            ...formData,
                                            name: String(value)
                                        })
                                    }
                                    required
                                />
                                <p className="mt-1 text-xs text-muted-foreground">
                                    The display name for this vendor
                                </p>
                            </div>

                            <Select
                                label="Vendor Type"
                                required
                                value={formData.type}
                                options={Object.values(VendorType).map(
                                    (type) => ({ label: type, value: type })
                                )}
                                onChange={(value) =>
                                    setFormData({
                                        ...formData,
                                        type: value as VendorType
                                    })
                                }
                            />

                            <Select
                                label="Source"
                                required
                                value={formData.source}
                                options={Object.values(VendorSource).map(
                                    (source) => ({
                                        label: source,
                                        value: source
                                    })
                                )}
                                onChange={(value) =>
                                    setFormData({
                                        ...formData,
                                        source: value as VendorSource
                                    })
                                }
                            />

                            <div>
                                <TextField
                                    label="External ID"
                                    type="text"
                                    value={formData.externalId || ''}
                                    onChange={(value) =>
                                        setFormData({
                                            ...formData,
                                            externalId: String(value)
                                        })
                                    }
                                />
                                <p className="mt-1 text-xs text-muted-foreground">
                                    ID from external vendor source (if
                                    applicable)
                                </p>
                            </div>

                            <div>
                                <TextField
                                    label="Display Order"
                                    type="number"
                                    value={formData.displayOrder ?? 0}
                                    onChange={(value) =>
                                        setFormData({
                                            ...formData,
                                            displayOrder: Number(value)
                                        })
                                    }
                                />
                                <p className="mt-1 text-xs text-muted-foreground">
                                    Lower numbers appear first in the Shop
                                </p>
                            </div>
                        </div>

                        <MultiCombobox<CountryCode>
                            label="Supported Countries"
                            placeholder="Select countries..."
                            required
                            value={formData.supportedCountries}
                            options={
                                Object.values(CountryCode).filter(
                                    (code) => code !== CountryCode.UNDEFINED
                                ) as CountryCode[]
                            }
                            getOptionLabel={(option) =>
                                `${getCountryLabel(option)} (${option})`
                            }
                            onChange={(value) =>
                                setFormData({
                                    ...formData,
                                    supportedCountries: value
                                })
                            }
                        />

                        <MultiCombobox<VendorCategorySupportDto>
                            label="Categories"
                            placeholder="Select categories..."
                            helperText="Categories help users find this vendor in the Shop"
                            loading={categoriesLoading}
                            value={selectedCategories}
                            options={availableCategories}
                            getOptionKey={(option) => option.id}
                            getOptionLabel={(option) =>
                                option.name.en || option.key
                            }
                            renderOption={(option) => (
                                <div>
                                    <p className="text-sm">
                                        {option.name.en || option.key}
                                    </p>
                                    {option.name.pl &&
                                        option.name.pl !== option.name.en && (
                                            <span className="text-xs text-muted-foreground">
                                                PL: {option.name.pl}
                                            </span>
                                        )}
                                </div>
                            )}
                            onChange={(value) =>
                                setFormData({
                                    ...formData,
                                    categoryIds: value.map((c) => c.id)
                                })
                            }
                        />
                    </div>
                </FormSection>

                <Accordion
                    type="multiple"
                    defaultValue={['shortDescription']}
                    className="flex flex-col gap-2"
                >
                    <AccordionItem value="shortDescription">
                        <AccordionTrigger>
                            <div>
                                <p className="font-medium">Short Description</p>
                                <span className="text-xs text-muted-foreground">
                                    Brief description shown in lists and
                                    previews
                                </span>
                            </div>
                        </AccordionTrigger>
                        <AccordionContent>
                            <LocalizedTextArea
                                label="Short Description"
                                value={formData.shortDescription || { en: '' }}
                                onChange={handleI18nTextChange(
                                    'shortDescription'
                                )}
                            />
                        </AccordionContent>
                    </AccordionItem>

                    <AccordionItem value="description">
                        <AccordionTrigger>
                            <div>
                                <p className="font-medium">Full Description</p>
                                <span className="text-xs text-muted-foreground">
                                    Detailed description of the vendor and their
                                    offerings
                                </span>
                            </div>
                        </AccordionTrigger>
                        <AccordionContent>
                            <LocalizedTextArea
                                label="Full Description"
                                value={formData.description || { en: '' }}
                                onChange={handleI18nTextChange('description')}
                            />
                        </AccordionContent>
                    </AccordionItem>

                    <AccordionItem value="redemptionDescription">
                        <AccordionTrigger>
                            <div>
                                <p className="font-medium">
                                    Redemption Instructions
                                </p>
                                <span className="text-xs text-muted-foreground">
                                    Instructions for users on how to redeem
                                    vouchers
                                </span>
                            </div>
                        </AccordionTrigger>
                        <AccordionContent>
                            <div className="flex flex-col gap-4">
                                <SwitchField
                                    id="redemptionInstructionsEnabled"
                                    checked={redemptionInstructionsEnabled}
                                    title="Enable"
                                    description="Provide custom instructions for redeeming vouchers from this vendor"
                                    onChange={setRedemptionInstructionsEnabled}
                                />
                                {redemptionInstructionsEnabled && (
                                    <LocalizedTextArea
                                        label="Redemption Instructions"
                                        value={
                                            formData.redemptionDescription || {
                                                en: ''
                                            }
                                        }
                                        onChange={handleI18nTextChange(
                                            'redemptionDescription'
                                        )}
                                    />
                                )}
                            </div>
                        </AccordionContent>
                    </AccordionItem>

                    <AccordionItem value="notAvailableReason">
                        <AccordionTrigger>
                            <div>
                                <p className="font-medium">
                                    Not Available Reason
                                </p>
                                <span className="text-xs text-muted-foreground">
                                    Reason shown to users when vendor is not
                                    available (optional)
                                </span>
                            </div>
                        </AccordionTrigger>
                        <AccordionContent>
                            <div className="flex flex-col gap-4">
                                <SwitchField
                                    id="notAvailableReasonEnabled"
                                    checked={notAvailableReasonEnabled}
                                    title="Enable"
                                    description="Marks all vendor products as unavailable for purchase and displays a custom message to users"
                                    onChange={setNotAvailableReasonEnabled}
                                />
                                {notAvailableReasonEnabled && (
                                    <LocalizedTextArea
                                        label="Not Available Reason"
                                        value={
                                            formData.notAvailableReason || {
                                                en: ''
                                            }
                                        }
                                        onChange={handleI18nTextChange(
                                            'notAvailableReason'
                                        )}
                                    />
                                )}
                            </div>
                        </AccordionContent>
                    </AccordionItem>
                </Accordion>
            </div>

            <StickyBar>
                <Button
                    variant="ghost"
                    onClick={handleCancel}
                    disabled={updateVendor.loading}
                >
                    Cancel
                </Button>
                <Button
                    onClick={handleSave}
                    loading={updateVendor.loading}
                    disabled={updateVendor.loading || !isFormValid}
                >
                    <Save />
                    {updateVendor.loading ? 'Saving...' : 'Save Changes'}
                </Button>
            </StickyBar>
        </>
    );
});

export default EditVendorForm;
